import { Box } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { SettingsDangerZoneRow } from "~/components/settings/DangerZoneWrapper";
import envConstants from "~/config/envConstants";
import { BULK_CLEAN_BINS_CONFIRMATION_PHRASE } from "~/features/preview/bins/binSettings.types";
import {
  getBulkCleanBinsDevHintKey,
  getCleanableFlaggedBins,
  hasProcessableFlaggedBins
} from "~/features/preview/bins/binSettings.utils";
import { selectWorkstationAutostoreGridId } from "~/redux/selectors/workstationsSelectors";
import { useGetFlaggedBinsQuery } from "~/redux/warehouse/autostoreGrid.hooks";

import { BulkCleanBinsModal } from "./BulkCleanBinsModal";

export function BulkCleanBinsDangerZone() {
  const { t } = useTranslation();
  const autostoreGridId = useAppSelector(selectWorkstationAutostoreGridId);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmationInput, setConfirmationInput] = useState("");

  const showDangerZone =
    envConstants.DEV_CHEATS_UAT === "true" ||
    envConstants.DEV_CHEATS_STAGING === "true";

  const { data: flaggedBins } = useGetFlaggedBinsQuery(
    {
      autostoreGridId: autostoreGridId!,
      workstationId: undefined
    },
    { skip: !showDangerZone || !autostoreGridId }
  );

  const cleanableBins = useMemo(
    () => getCleanableFlaggedBins(flaggedBins),
    [flaggedBins]
  );

  const canProcessFlaggedBins = useMemo(
    () => hasProcessableFlaggedBins(cleanableBins),
    [cleanableBins]
  );

  const hintKey = useMemo(
    () => getBulkCleanBinsDevHintKey(cleanableBins),
    [cleanableBins]
  );

  const canOpenModal =
    confirmationInput.trim() === BULK_CLEAN_BINS_CONFIRMATION_PHRASE &&
    !!autostoreGridId &&
    canProcessFlaggedBins;

  if (!showDangerZone) {
    return null;
  }

  const handleOpenModal = () => {
    if (!canOpenModal) return;
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setConfirmationInput("");
  };

  return (
    <Box component="section" aria-label={t("bulk clean bins dev title")}>
      <SettingsDangerZoneRow
        title={t("bulk clean bins dev title")}
        hint={t(hintKey)}
        confirmationPhrase={BULK_CLEAN_BINS_CONFIRMATION_PHRASE}
        confirmationValue={confirmationInput}
        onConfirmationChange={setConfirmationInput}
        actionLabel={t("open bulk clean bins tool")}
        onAction={handleOpenModal}
        actionDisabled={!canOpenModal}
      />

      <BulkCleanBinsModal
        open={modalOpen}
        onClose={handleCloseModal}
        autostoreGridId={autostoreGridId}
      />
    </Box>
  );
}
